import { css } from 'styled-components'
import { fontBody, fontDisplay } from './fonts'

/**
 * The type ramp — eleven px steps shared by every surface. Always px: the mobile
 * handoffs are specified in px and the newest webapp code already is. Pick the
 * step by role, never by "what looks close". A figure or a glyph that wants to be
 * big takes `display`; a hero title takes `hero`, `heroSm` below the tablet
 * breakpoint. Nothing is written as a literal: `__tests__/fontSizeRamp.test.ts`
 * fails on one.
 */
export const fontSizes = {
	/** Eyebrows, badges, pills, timestamps. */
	micro: '12px',
	/** Captions, helper and meta text. */
	caption: '13px',
	/** Secondary copy, buttons, table cells. */
	small: '14px',
	/** Field text, subtitles, list rows. */
	base: '15px',
	/** Body copy. */
	body: '16px',
	/** Card and modal titles. */
	title: '18px',
	/** Section headings. */
	heading: '22px',
	/** Band titles; the page title on phones. */
	headingLg: '24px',
	/** Page title. */
	display: '28px',
	/** The hero title's tablet size; the admin stat figure. */
	heroSm: '36px',
	/** Hero title: the sign-in, join and public itinerary heroes. */
	hero: '44px',
} as const

/**
 * Tracking: display type pulls in a hair, eyebrows and codes open up;
 * everything else sits at the font's own spacing. A styles file never writes
 * an em.
 */
export const tracking = {
	tight: '-0.01em',
	wide: '0.06em',
} as const

/**
 * Leading, by what the line is: none for a glyph or a single-line figure,
 * tight for display type, heading for headings and titles, snug for labels,
 * body for anything read as text.
 */
export const leading = {
	none: 1,
	tight: 1.1,
	heading: 1.2,
	snug: 1.3,
	body: 1.5,
} as const

export type FontSizeKey = keyof typeof fontSizes
export type TrackingKey = keyof typeof tracking
export type LeadingKey = keyof typeof leading

/**
 * Role recipes: family, weight, size and leading in one mixin so a heading or a
 * label can't drift on a single axis. A figure or a mark that needs one axis
 * different — a 900 weight, no leading, a bigger step — takes the nearest role
 * and overrides that one line; nothing sets a family by hand. Lato is reserved for the display roles
 * (headings, buttons, labels); Yantramanav carries everything read as text.
 * index.html loads Lato at 400/700/900 (the family has no 800) and Yantramanav
 * at 400/500/700 — only those weights render as written.
 */
export const text = {
	/** Hero title: the sign-in, join and overview heroes. */
	hero: css`
		font-family: ${fontDisplay};
		font-weight: 900;
		font-size: ${fontSizes.hero};
		line-height: ${leading.tight};
		letter-spacing: ${tracking.tight};
	`,
	/** Page title. */
	display: css`
		font-family: ${fontDisplay};
		font-weight: 900;
		font-size: ${fontSizes.display};
		line-height: ${leading.tight};
		letter-spacing: ${tracking.tight};
	`,
	/** Section heading. */
	heading: css`
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: ${fontSizes.heading};
		line-height: ${leading.heading};
		letter-spacing: ${tracking.tight};
	`,
	/** Card and modal title. */
	title: css`
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: ${fontSizes.title};
		line-height: ${leading.heading};
	`,
	/** Tile label, button text. */
	label: css`
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: ${fontSizes.base};
		line-height: ${leading.snug};
	`,
	/** Small label: chips, list-row names, compact meta. */
	labelSm: css`
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: ${fontSizes.small};
		line-height: ${leading.snug};
	`,
	/** Pill and badge text. */
	badge: css`
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: ${fontSizes.micro};
		line-height: ${leading.snug};
	`,
	/** Uppercase section label. */
	eyebrow: css`
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: ${fontSizes.micro};
		line-height: ${leading.snug};
		letter-spacing: ${tracking.wide};
		text-transform: uppercase;
	`,
	/** Body copy. */
	body: css`
		font-family: ${fontBody};
		font-weight: 400;
		font-size: ${fontSizes.body};
		line-height: ${leading.body};
	`,
	/** Field text, subtitles, list rows. */
	bodySm: css`
		font-family: ${fontBody};
		font-weight: 400;
		font-size: ${fontSizes.base};
		line-height: ${leading.body};
	`,
	/** Secondary copy, helper text. */
	bodyXs: css`
		font-family: ${fontBody};
		font-weight: 400;
		font-size: ${fontSizes.small};
		line-height: ${leading.body};
	`,
	/** Captions, metadata, timestamps. */
	caption: css`
		font-family: ${fontBody};
		font-weight: 500;
		font-size: ${fontSizes.caption};
		line-height: ${leading.body};
	`,
} as const

export type TextRole = keyof typeof text
